import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewEncapsulation,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { KycConfig } from "@obg/ngx.common";
import {
	AuthTransactionConfigService,
	CORE_ROUTE,
	Jurisdiction,
	JurisdictionSelector,
	LoginMethod,
	MarketSelector,
	SessionSelector,
	UiInteraction,
	VerificationStatus,
} from "@obg/ngx.core";
import { CoreContentState } from "@obg/ngx.core.content";
import { coerceBoolean, TelemetryService } from "@obg/ngx.global.evo";
import { TrackingContext } from "@obg/ngx.ui.core";
import { ignoreNil } from "@obg/rxjs";
import { combineLatest, merge, Subscription } from "rxjs";
import { filter, tap } from "rxjs/operators";

import { CommonAuthTelemetryEventName, LoginFeatureState, SgaLoginSelector, SgaLoginTab } from "../../sdk";
import { LoginFlowService } from "../flow/login-flow.service";
import { LoginSupportInteractionEvent } from "../login-ui/login-ui.const";
import { LoginUiConfigService } from "../login.config";
import { LoginFormType, LoginTab } from "../login.model";
import { SmartIdLoginService } from "../smart-id/smart-id-login.service";

@Component({
	selector: "obg-m-login-container",
	templateUrl: "./login.container.html",
	styleUrls: ["./login.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class LoginContainer implements OnInit, OnDestroy {
	@Input()
	@coerceBoolean()
	showSgaLoginTitle = false;

	@Input()
	@coerceBoolean()
	autoFocus = false;

	@Input()
	@coerceBoolean()
	showGenericLoginTitle = false;

	@Input()
	@coerceBoolean()
	stretchedTabs = false;

	@Input()
	@coerceBoolean()
	errorsInAlert = false;

	@Output() loginSuccess = new EventEmitter();
	@Output() busy = new EventEmitter<boolean>();
	@Output() registerButtonClick = new EventEmitter();
	@Output() forgotPasswordButtonClick = new EventEmitter();
	@Output() isSgaMode = new EventEmitter<boolean>();

	@Input()
	@coerceBoolean()
	allLinksExternal = false;

	@Input()
	@coerceBoolean()
	disallowedRedirection = false;

	@Input()
	@coerceBoolean()
	disallowLogin = false;

	@Input() hideEmailPasswordLoginForDga = false;
	@Input() hideSgaLogin = false;

	@HostBinding("class.obg-m-login") hostCssClass = true;
	@HostBinding("class.obg-m-login-sga-mode") showSgaView = false;

	isLoading = false;
	registerRouteLink = CORE_ROUTE.registration.register;
	createAccountTrackingData: UiInteraction = {
		context: TrackingContext.LoginSupportInteractionContext,
		event: LoginSupportInteractionEvent.Register,
	};
	selectedTab = LoginTab.Generic;
	bankIdTab = LoginTab.BankID;
	disableTab: boolean | undefined;
	shouldShowRegisterButton: boolean;
	secureStartEnabled: boolean;
	loginFormType = LoginFormType.Generic;
	showIauiSelfExclusionText: boolean | undefined;

	private data$$: Subscription | undefined;
	private tabName: string | undefined;

	constructor(
		private store: Store<CoreContentState & LoginFeatureState>,
		private telemetryService: TelemetryService,
		private changeDetector: ChangeDetectorRef,
		private authTransactionConfig: AuthTransactionConfigService,
		private loginFlowService: LoginFlowService,
		private marketSelector: MarketSelector,
		private config: LoginUiConfigService,
		private sessionSelector: SessionSelector,
		private kycConfig: KycConfig,
		private smartIdLoginService: SmartIdLoginService,
		private sgaLoginSelector: SgaLoginSelector
	) {
		this.shouldShowRegisterButton = this.config.getShowRegisterButton();
		this.secureStartEnabled = this.authTransactionConfig.getBankIdSecureStartEnabled();
	}

	ngOnInit() {
		this.telemetryService.track({
			event: CommonAuthTelemetryEventName.LoginFormOpen,
		});

		const loginFormTypeSelected$ = combineLatest([
			this.store.select(this.sessionSelector.isAuthenticated()),
			this.store.select(JurisdictionSelector.get).pipe(ignoreNil()),
			this.store.select(this.marketSelector.getLanguageCode()),
			this.store.select(this.sgaLoginSelector.selectedLoginTab()),
		]).pipe(
			filter(([isAuth]) => !isAuth),
			tap(([, jurisdiction, languageCode, sgaLoginSelectedLoginTab]) => {
				this.showIauiSelfExclusionText = jurisdiction === Jurisdiction.Iaui;
				this.setFormAndTab(jurisdiction, languageCode, sgaLoginSelectedLoginTab);
				this.showSgaView = this.loginFormType === LoginFormType.SGA;
				this.isSgaMode.emit(this.showSgaView);
				this.selectedTabChanged(this.selectedTab);
				this.setSkipSgaVerification(this.selectedTab);
			})
		);

		const loginTransactionsVerificationStatus$ = combineLatest([
			this.store.select(JurisdictionSelector.get).pipe(ignoreNil()),
			this.smartIdLoginService.loginTransactionsVerificationStatus$(),
		]).pipe(
			filter(() => this.kycConfig.getIsSmartIdVerificationEnabled()),
			tap(([jurisdiction, status]) => {
				this.loginFlowService.skipSgaVerification = true;

				if (status === VerificationStatus.Pending) {
					this.showIauiSelfExclusionText = false;
					this.shouldShowRegisterButton = false;
					this.disableTab = true;
					return;
				}

				if (jurisdiction === Jurisdiction.Iaui) {
					this.showIauiSelfExclusionText = true;
				}

				this.shouldShowRegisterButton = true;
				this.disableTab = false;
			})
		);

		this.data$$ = merge(loginFormTypeSelected$, loginTransactionsVerificationStatus$).subscribe(() =>
			this.changeDetector.markForCheck()
		);
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();

		this.loginFlowService.skipSgaVerification = false;
	}

	selectedTabClick(index: LoginTab) {
		this.selectedTabChanged(index);
		this.trackTabClick(index);
	}

	private selectedTabChanged(index: LoginTab) {
		this.selectedTab = index;
	}

	private setSkipSgaVerification(index: LoginTab) {
		// if BankID login tab is active now, BankID verification should not fire
		// as it's being handled by BankID login tab itself
		this.loginFlowService.skipSgaVerification = index === LoginTab.BankID;
	}

	private setFormAndTab(jurisdiction: Jurisdiction, languageCode: string, sgaLoginSelectedLoginTab?: SgaLoginTab) {
		const loginIsEnabled = this.authTransactionConfig.getLoginEnabled();

		if ((loginIsEnabled && jurisdiction === Jurisdiction.Sga) || (languageCode === "sv" && !this.hideSgaLogin)) {
			const selectSgaTab = sgaLoginSelectedLoginTab === SgaLoginTab.Generic ? LoginTab.Generic : LoginTab.BankID;
			this.selectedTab = selectSgaTab;
			this.loginFormType = LoginFormType.SGA;
		} else if (jurisdiction === Jurisdiction.Dga) {
			this.loginFormType = LoginFormType.DGA;
			this.shouldShowRegisterButton = false;
		} else if (this.kycConfig.getIsSmartIdVerificationEnabled()) {
			this.loginFormType = LoginFormType.SmartId;
		}
	}

	private trackTabClick(index: LoginTab) {
		this.tabName = LoginMethod.BankId;
		this.isSgaMode.emit(index === LoginTab.BankID);
		if (this.kycConfig.getIsSmartIdVerificationEnabled()) {
			this.tabName = this.loginFormType;
		}

		this.telemetryService.track({
			event: CommonAuthTelemetryEventName.LoginTabClick,
			TabName: index >= 1 ? this.tabName : LoginMethod.Credentials,
		});
	}
}
